export type ProfessorConfigureSegment =
  | 'scenario'
  | 'timers'
  | 'communications'
  | 'resource-limits'
  | 'advisors'
  | 'ai-players'
  | 'shared-advisor-settings'
  | 'api-access'
  | 'charter'
  | 'world-graph'

export const PROFESSOR_CONFIGURE_SEGMENTS = {
  SCENARIO: 'scenario',
  TIMERS: 'timers',
  COMMUNICATIONS: 'communications',
  RESOURCE_LIMITS: 'resource-limits',
  ADVISORS: 'advisors',
  AI_PLAYERS: 'ai-players',
  SHARED_ADVISOR_SETTINGS: 'shared-advisor-settings',
  API_ACCESS: 'api-access',
  CHARTER: 'charter',
  WORLD_GRAPH: 'world-graph',
} as const satisfies Record<string, ProfessorConfigureSegment>

const ROUTES = {
  // Root
  ROOT: '/',

  // Authentication routes
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  VERIFY_CODE: '/verify-code',
  CONFIRM_EMAIL: '/users/confirm-email',
  VERIFICATION: {
    EMAIL: '/confirmation',
    OTP: '/otp-required',
    QR: '/qr-verification',
  },

  // Guest link landing
  GUEST: '/guest',

  // User profile
  PROFILE: '/profile',

  // Student routes
  STUDENT: {
    DASHBOARD: '/player-dashboard',
    JOIN_ROOM: (roomId: string) => `/player-dashboard/${roomId}`,
    WAITING_ROOM: (roomId: string, inviteCode?: string) => {
      const base = `/player-dashboard/${roomId}/waiting-room`
      return inviteCode ? `${base}?inviteCode=${inviteCode}` : base
    },
    GAME: (roomId: string, inviteCode?: string) => {
      const base = `/player-dashboard/${roomId}/player`
      return inviteCode ? `${base}?inviteCode=${inviteCode}` : base
    },
    CHARTER_PREP: (roomId: string) => `/player-dashboard/${roomId}/charter/prep`,
    CHARTER_GROUP: (roomId: string) => `/player-dashboard/${roomId}/charter/group`,
    CHARTER_VIEW: (roomId: string) => `/player-dashboard/${roomId}/charter/view`,
    CHARTER_VERSIONS: (roomId: string) => `/player-dashboard/${roomId}/charter/versions`,
    /** Landing page for a standalone Charter Class — replaces the game waiting
     *  room, and forwards to prep/group/charter once the state is known. */
    CHARTER_CLASS: (roomId: string, inviteCode?: string) => {
      const base = `/player-dashboard/${roomId}/class`
      return inviteCode ? `${base}?inviteCode=${inviteCode}` : base
    },
  },

  // Professor routes
  PROFESSOR: {
    ROOMS: '/rooms',
    JOIN_ROOM: (roomId: string) => `/room/join-room/${roomId}`,
    ROOM_DETAILS: (roomId: string) => `/rooms/${roomId}`,
    ROOM_ADJUDICATE: (roomId: string) => `/rooms/${roomId}/adjudicate`,
    ROOM_SETTINGS: (roomId: string) => `/rooms/${roomId}/settings`,
    ROOM_CONFIGURATION: (roomId: string) => `/rooms/${roomId}/configuration`,
    ROOM_ADVISOR_SETTINGS: (roomId: string) => `/rooms/${roomId}/advisor-settings`,
    ROOM_TIMER_SETTINGS: (roomId: string) => `/rooms/${roomId}/timer-settings`,
    ROOM_COMMUNICATIONS: (roomId: string) => `/rooms/${roomId}/communications`,
    ROOM_RESOURCE_LIMITS: (roomId: string) =>
      `/rooms/${roomId}/resource-limits`,
    ROOM_CONFIGURE: (roomId: string) => `/rooms/${roomId}/configure`,
    ROOM_CONFIGURE_SEGMENT: (
      roomId: string,
      segment: ProfessorConfigureSegment
    ) => `/rooms/${roomId}/configure/${segment}`,
    ROOM_CONFIGURE_SCENARIO: (roomId: string) =>
      `/rooms/${roomId}/configure/scenario`,
    ROOM_CONFIGURE_TIMERS: (roomId: string) =>
      `/rooms/${roomId}/configure/timers`,
    ROOM_CONFIGURE_COMMUNICATIONS: (roomId: string) =>
      `/rooms/${roomId}/configure/communications`,
    ROOM_CONFIGURE_RESOURCE_LIMITS: (roomId: string) =>
      `/rooms/${roomId}/configure/resource-limits`,
    ROOM_CONFIGURE_ADVISORS: (roomId: string) =>
      `/rooms/${roomId}/configure/advisors`,
    ROOM_CONFIGURE_AI_PLAYERS: (roomId: string) =>
      `/rooms/${roomId}/configure/ai-players`,
    ROOM_CONFIGURE_SHARED_ADVISOR_SETTINGS: (roomId: string) =>
      `/rooms/${roomId}/configure/shared-advisor-settings`,
    ROOM_CONFIGURE_API_ACCESS: (roomId: string) =>
      `/rooms/${roomId}/configure/api-access`,
    ROOM_CONFIGURE_WORLD_GRAPH: (roomId: string) =>
      `/rooms/${roomId}/configure/world-graph`,
    ROOM_PLAYER_RESOURCE_LIMITS: (roomId: string) =>
      `/rooms/${roomId}/player-resource-limits`,
    GAME_SETUP_WIZARD: (roomId: string) => `/rooms/${roomId}/game-setup-wizard`,
    QUICK_CONTROLS: (roomId: string) => `/rooms/${roomId}/quick-controls`,
    MONITORING: (roomId: string) => `/rooms/${roomId}/monitoring`,
    ROOM_WORLD: (roomId: string) => `/rooms/${roomId}/world`,
    GAME_ANALYSIS: (roomId: string) => `/rooms/${roomId}/game-analysis`,
    GAME_SUMMARY: (roomId: string) => `/rooms/${roomId}/game-summary`,
    SIMULATION_PLAYERS: (roomId: string) =>
      `/rooms/${roomId}/simulation-players`,
    ROOM_CONFIGURE_CHARTER: (roomId: string) =>
      `/rooms/${roomId}/configure/charter`,
    // Standalone Charter Classes (no game attached).
    CHARTER_CLASSES: '/charter-classes',
    CHARTER_CLASS: (roomId: string) => `/charter-classes/${roomId}`,

    ROOM_CHARTER_MONITORING: (roomId: string) =>
      `/rooms/${roomId}/charter-monitoring`,
    ROOM_CHARTER_PREP_MONITOR: (roomId: string, memberId: string) =>
      `/rooms/${roomId}/charter-monitoring/prep/${memberId}`,
    ROOM_CHARTER_GROUP_MONITOR: (roomId: string, teamId: string) =>
      `/rooms/${roomId}/charter-monitoring/group/${teamId}`,

    SETTINGS: '/settings',
    GAME: (
      roomId: string,
      params?: {
        playerId?: string
        teamId?: string
        negotiationId?: string
        inviteCode?: string
        simulation?: string
        panel?: 'chat' | 'game'
      }
    ) => {
      const baseUrl = `/prof-dashboard/${roomId}/player`

      if (!params) return baseUrl

      const queryParams = []
      if (params.playerId) queryParams.push(`playerId=${params.playerId}`)
      if (params.teamId) queryParams.push(`teamId=${params.teamId}`)
      if (params.negotiationId)
        queryParams.push(`negotiationId=${params.negotiationId}`)
      if (params.inviteCode) queryParams.push(`inviteCode=${params.inviteCode}`)
      if (params.simulation) queryParams.push(`simulation=${params.simulation}`)
      if (params.panel) queryParams.push(`panel=${params.panel}`)

      return queryParams.length > 0
        ? `${baseUrl}?${queryParams.join('&')}`
        : baseUrl
    },
  },

  // Error routes
  NOT_FOUND: '*',
}

export default ROUTES
